import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  User, Mail, Phone, Shield, History, Settings, 
  ChevronRight, CheckCircle, AlertCircle, X, Car, AtSign, MapPin, Clock
} from 'lucide-react';
import VerificationCenter from './VerificationCenter';
import { toast } from '../../hooks/use-toast';
import { getRiderRides, getDriverRides, formatRideForDisplay } from '../../services/rideService';
import { refreshUserProfile } from '../../services/userService';

const ProfilePage = ({ user, onUpdateUser, onClose }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showVerification, setShowVerification] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [rideHistory, setRideHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user.first_name || user.firstName || '',
    middleName: user.middle_name || user.middleName || '',
    lastName: user.last_name || user.lastName || '',
    email: user.email || '',
    phone: user.phone || ''
  });

  const isDriver = user.role === 'driver';
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);

  // Fetch ride history when history tab is opened
  useEffect(() => {
    if (activeTab === 'history' && !hasLoadedHistory && !loadingHistory) {
      fetchRideHistory();
    }
  }, [activeTab]);

  const fetchRideHistory = async () => {
    setLoadingHistory(true);
    try {
      let result;
      if (isDriver) {
        result = await getDriverRides(user.id);
      } else {
        result = await getRiderRides(user.id);
      }

      if (result.success && result.data) {
        const formattedRides = result.data.map(ride => formatRideForDisplay(ride));
        setRideHistory(formattedRides);
        setHasLoadedHistory(true);
      } else {
        toast({
          title: 'Error fetching ride history',
          description: result.message || 'Could not load your rides',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching ride history:', error);
      toast({
        title: 'Error',
        description: 'Failed to load ride history',
        variant: 'destructive'
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  // Calculate statistics from ride history - recalculate when rideHistory changes
  const getStats = () => {
    const completedRides = rideHistory.filter(r => r.status === 'completed');
    const totalEarned = completedRides.reduce((sum, r) => sum + (r.amount || 0), 0);
    const avgRating = completedRides.filter(r => r.rating).reduce((sum, r, _, arr) => 
      sum + r.rating / arr.length, 0
    );

    return {
      totalRides: completedRides.length,
      totalEarned: totalEarned,
      avgRating: avgRating ? avgRating.toFixed(1) : 'N/A'
    };
  };

  const stats = getStats();

  const getVerificationStatusBadge = () => {
    const status = user.verification_status || user.verificationStatus;
    const configs = {
      verified: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Verified' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, text: 'Pending' },
      pending_review: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle, text: 'Under Review' },
      rejected: { color: 'bg-red-100 text-red-800', icon: AlertCircle, text: 'Action Required' }
    };
    
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
      <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full ${config.color}`}>
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{config.text}</span>
      </div>
    );
  };

  const handleSaveProfile = () => {
    onUpdateUser({...user, ...formData});
    setIsEditing(false);
    toast({ title: 'Profile updated successfully' });
  };

  const handleVerificationComplete = async (verificationResult) => {
    // Refresh user data from server to get latest verification status
    const result = await refreshUserProfile(user.id);
    if (result.success) {
      onUpdateUser(result.user);
      toast({ 
        title: 'Verification Submitted', 
        description: 'Your documents are being reviewed. We\'ll notify you once complete.' 
      });
    }
    setShowVerification(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="max-w-4xl w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 flex-shrink-0">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Profile & Settings</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'profile'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab('verification')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'verification'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Verification
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'history'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              History
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* User Info Card */}
              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
                    <User className="w-10 h-10 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold">{user.first_name || user.firstName} {user.last_name || user.lastName}</h3>
                    <p className="text-green-100">{isDriver ? 'Driver' : 'Rider'}</p>
                    {user.username && (
                      <p className="text-green-100 text-sm mt-1">@{user.username}</p>
                    )}
                    {(user.phone_verified || user.phoneVerified) && (
                      <div className="flex items-center space-x-2 mt-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">Phone Verified</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              {/* Personal Details */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Personal Information</h3>
                  {!isEditing && (
                    <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
                      Edit
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700">First Name</Label>
                      <Input
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        disabled={!isEditing}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700">Last Name</Label>
                      <Input
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        disabled={!isEditing}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-700">Middle Name</Label>
                    <Input
                      value={formData.middleName}
                      onChange={(e) => setFormData({...formData, middleName: e.target.value})}
                      disabled={!isEditing}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-700">Username</Label>
                    <div className="flex items-center space-x-2 mt-2">
                      <div className="relative flex-1">
                        <AtSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input 
                          value={user.username || 'Not set'} 
                          disabled 
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
                  </div>

                  <div>
                    <Label className="text-gray-700">Phone Number</Label>
                    <div className="flex items-center space-x-2 mt-2">
                      <Input value={formData.phone} disabled className="flex-1" />
                      {(user.phone_verified || user.phoneVerified) && <CheckCircle className="w-5 h-5 text-green-600" />}
                    </div>
                  </div>

                  <div>
                    <Label className="text-gray-700">Email Address</Label>
                    <div className="flex items-center space-x-2 mt-2">
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        disabled={!isEditing}
                        placeholder="Add your email"
                        className="flex-1"
                      />
                      {(user.email_verified || user.emailVerified) && <CheckCircle className="w-5 h-5 text-green-600" />}
                      {!(user.email_verified || user.emailVerified) && formData.email && (
                        <Button size="sm" variant="outline">Verify</Button>
                      )}
                    </div>
                  </div>
                </div>

                {isEditing && (
                  <div className="flex space-x-3 mt-6">
                    <Button onClick={() => setIsEditing(false)} variant="outline" className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleSaveProfile} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>

              {/* Driver Vehicle Info */}
              {isDriver && user.vehicle && (
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Vehicle Information</h3>
                  <Card className="p-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Car className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-800">{user.vehicle.color} {user.vehicle.make} {user.vehicle.model}</p>
                        <p className="text-sm text-gray-600">{user.vehicle.plate}</p>
                        {user.vehicle.year && <p className="text-xs text-gray-500">{user.vehicle.year}</p>}
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Verification Tab */}
          {activeTab === 'verification' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-6 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Verification Status</h3>
                  {getVerificationStatusBadge()}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(user.phone_verified || user.phoneVerified) ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Phone className={`w-5 h-5 ${(user.phone_verified || user.phoneVerified) ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Phone Verification</p>
                      <p className="text-sm text-gray-500">{user.phone}</p>
                    </div>
                  </div>
                  {(user.phone_verified || user.phoneVerified) ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <Button size="sm">Verify</Button>
                  )}
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(user.email_verified || user.emailVerified) ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Mail className={`w-5 h-5 ${(user.email_verified || user.emailVerified) ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Email Verification</p>
                      <p className="text-sm text-gray-500">{user.email || 'Not provided'}</p>
                    </div>
                  </div>
                  {(user.email_verified || user.emailVerified) ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : user.email ? (
                    <Button size="sm">Verify</Button>
                  ) : (
                    <Button size="sm" variant="outline">Add Email</Button>
                  )}
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(user.id_verified || user.idVerified) ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Shield className={`w-5 h-5 ${(user.id_verified || user.idVerified) ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Identity Verification</p>
                      <p className="text-sm text-gray-500">ID & Liveness check</p>
                    </div>
                  </div>
                  {(user.verification_status || user.verificationStatus) === 'verified' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {(user.verification_status || user.verificationStatus) !== 'verified' && (
                <Button
                  onClick={() => setShowVerification(true)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
                >
                  {(user.verification_status || user.verificationStatus) === 'pending' ? 'Start Verification' : 'Continue Verification'}
                </Button>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              {/* Statistics Card */}
              {rideHistory.length > 0 && (
                <Card className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Your Statistics</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{stats.totalRides}</p>
                      <p className="text-xs text-gray-600">Total Rides</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">MWK {stats.totalEarned.toLocaleString()}</p>
                      <p className="text-xs text-gray-600">{isDriver ? 'Earned' : 'Spent'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{stats.avgRating}</p>
                      <p className="text-xs text-gray-600">Avg Rating</p>
                    </div>
                  </div>
                </Card>
              )}

              {loadingHistory ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading ride history...</p>
                </div>
              ) : rideHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No ride history yet</p>
                  <p className="text-sm text-gray-400 mt-2">Your completed rides will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rideHistory.map((ride) => (
                    <Card key={ride.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${ride.statusColor}`}>
                              {ride.statusText}
                            </span>
                            <span className="text-xs font-medium bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {ride.rideType}
                            </span>
                          </div>
                          
                          <div className="space-y-1 mb-2">
                            <div className="flex items-start space-x-2">
                              <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-gray-800 font-medium">{ride.from}</p>
                            </div>
                            <div className="flex items-start space-x-2">
                              <MapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-gray-800 font-medium">{ride.to}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            <span>{ride.date}</span>
                          </div>
                          
                          {ride.rating && (
                            <div className="mt-2 flex items-center space-x-1">
                              {[...Array(5)].map((_, i) => (
                                <span key={i} className={i < ride.rating ? 'text-yellow-400' : 'text-gray-300'}>
                                  ★
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right ml-4">
                          <p className="text-lg font-bold text-gray-800">MWK {ride.amount?.toLocaleString()}</p>
                          {ride.paymentStatus && (
                            <p className="text-xs text-gray-500 mt-1 capitalize">{ride.paymentStatus}</p>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Verification Modal */}
      {showVerification && (
        <VerificationCenter
          user={user}
          onVerificationComplete={handleVerificationComplete}
          onClose={() => setShowVerification(false)}
        />
      )}
    </div>
  );
};

export default ProfilePage;
